import React, { useState } from "react";
import { useIntl } from "react-intl";
import { Lock, Eye, EyeOff, Save } from "lucide-react";
import { changePassword } from "../../services/lewooAuth";
import { useDispatch } from "react-redux";
import { restoreAuthSession } from "../../redux/actions/authActions";

const ForcePasswordChange = ({ visible, onClose, onSuccess }) => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (newPassword !== confirmPassword) {
      setError(intl.formatMessage({ id: "admin.password.mismatch" }));
      return;
    }

    if (newPassword.length < 8) {
      setError(intl.formatMessage({ id: "admin.password.tooShort" }));
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      await changePassword(currentPassword, newPassword);
      await dispatch(restoreAuthSession());
      onSuccess && onSuccess();
    } catch (err) {
      setError(err.message || intl.formatMessage({ id: "admin.password.changeError" }));
    } finally {
      setSubmitting(false);
    }
  };

  if (!visible) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 p-8">
        <div className="flex items-center justify-center w-12 h-12 rounded-full bg-amber-50 border border-amber-200 mx-auto mb-4">
          <Lock className="w-6 h-6 text-amber-600" />
        </div>
        <h2 className="text-xl font-display font-bold text-neutral-900 text-center mb-2">
          {intl.formatMessage({ id: "admin.password.changeRequired" })}
        </h2>
        <p className="text-neutral-500 text-sm text-center mb-6">
          {intl.formatMessage({ id: "admin.password.changeRequiredDesc" })}
        </p>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1.5">
              {intl.formatMessage({ id: "admin.password.current" })}
            </label>
            <div className="relative">
              <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
              <input
                type={showCurrent ? "text" : "password"}
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                placeholder={intl.formatMessage({ id: "admin.password.currentPlaceholder" })}
                required
                disabled={submitting}
                className="w-full pl-10 pr-11 py-2.5 bg-neutral-50 border border-neutral-200 rounded-lg outline-none text-sm text-neutral-900 placeholder:text-neutral-400 disabled:opacity-50"
              />
              <button
                type="button"
                onClick={() => setShowCurrent(!showCurrent)}
                disabled={submitting}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-neutral-600 disabled:opacity-50"
              >
                {showCurrent ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1.5">
              {intl.formatMessage({ id: "admin.password.new" })}
            </label>
            <div className="relative">
              <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
              <input
                type={showNew ? "text" : "password"}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder={intl.formatMessage({ id: "admin.password.newPlaceholder" })}
                required
                disabled={submitting}
                minLength={8}
                className="w-full pl-10 pr-11 py-2.5 bg-neutral-50 border border-neutral-200 rounded-lg outline-none text-sm text-neutral-900 placeholder:text-neutral-400 disabled:opacity-50"
              />
              <button
                type="button"
                onClick={() => setShowNew(!showNew)}
                disabled={submitting}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-neutral-600 disabled:opacity-50"
              >
                {showNew ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
            <p className="text-xs text-neutral-400 mt-1">
              {intl.formatMessage({ id: "admin.password.minLength" })}
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1.5">
              {intl.formatMessage({ id: "admin.password.confirm" })}
            </label>
            <div className="relative">
              <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
              <input
                type={showConfirm ? "text" : "password"}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder={intl.formatMessage({ id: "admin.password.confirmPlaceholder" })}
                required
                disabled={submitting}
                className="w-full pl-10 pr-11 py-2.5 bg-neutral-50 border border-neutral-200 rounded-lg outline-none text-sm text-neutral-900 placeholder:text-neutral-400 disabled:opacity-50"
              />
              <button
                type="button"
                onClick={() => setShowConfirm(!showConfirm)}
                disabled={submitting}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-neutral-600 disabled:opacity-50"
              >
                {showConfirm ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-4 py-3 flex items-start gap-3">
              <span className="text-red-600 font-bold">!</span>
              <span>{error}</span>
            </div>
          )}

          <button
            type="submit"
            disabled={submitting}
            className="w-full bg-light hover:bg-light-200 text-white font-medium py-2.5 rounded-lg transition-all duration-200 disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {submitting ? (
              <>
                <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
                {intl.formatMessage({ id: "admin.password.saving" })}
              </>
            ) : (
              <>
                <Save size={16} />
                {intl.formatMessage({ id: "admin.password.save" })}
              </>
            )}
          </button>
        </form>
      </div>
    </div>
  );
};

export default ForcePasswordChange;
